/**
 * Report Dialog Component
 * A user-friendly multi-step dialog for reporting content with multiple reasons
 * and optional description. Only authenticated users can submit reports.
 */

import { useState } from 'react';
import { useSubmitReport, useCheckReport } from '../../hooks/use-admin';
import { useAuthStore } from '../../stores';
import DOMPurify from 'dompurify';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import {
  Flag,
  AlertTriangle,
  Image,
  Video,
  ChevronRight,
  Check,
  Loader2,
  LogIn,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Report reasons with descriptions - organized by severity/frequency
const REPORT_REASONS = [
  // Common reasons (shown by default)
  {
    value: 'spam',
    label: 'Spam',
    description: 'Unwanted promotional content or repetitive posts',
    common: true,
  },
  {
    value: 'harassment',
    label: 'Harassment',
    description: 'Bullying, threats, or targeted attacks',
    common: true,
  },
  {
    value: 'inappropriate',
    label: 'Inappropriate Content',
    description: 'Content not suitable for this platform',
    common: true,
  },
  {
    value: 'misinformation',
    label: 'Misinformation',
    description: 'False or misleading information',
    common: true,
  },
  {
    value: 'scam',
    label: 'Scam/Fraud',
    description: 'Deceptive schemes to steal money or information',
    common: true,
  },
  // Less common reasons
  {
    value: 'hate_speech',
    label: 'Hate Speech',
    description: 'Content promoting hatred against groups',
  },
  {
    value: 'copyright',
    label: 'Copyright Violation',
    description: 'Using content without permission',
  },
  {
    value: 'stolen',
    label: 'Stolen Content',
    description: 'Content taken from another creator',
  },
  {
    value: 'pornographic',
    label: 'Adult/Sexual Content',
    description: 'Explicit or sexual material',
  },
  {
    value: 'violent',
    label: 'Violence/Gore',
    description: 'Graphic violence or disturbing imagery',
  },
  {
    value: 'doxxing',
    label: 'Privacy Violation',
    description: 'Sharing private information without consent',
  },
  {
    value: 'self_harm_promotion',
    label: 'Self-Harm Promotion',
    description: 'Content promoting self-injury or suicide',
  },
  {
    value: 'illegal',
    label: 'Illegal Content',
    description: 'Content that violates laws',
  },
  {
    value: 'csam',
    label: 'Child Safety',
    description: 'Content exploiting or endangering minors',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Other issues not listed above',
    common: true,
  },
];

// Media-specific reasons for quicker access
const MEDIA_REPORT_REASONS = [
  {
    value: 'pornographic',
    label: 'Adult/Sexual Content',
    description: 'Explicit or sexual material',
    common: true,
  },
  {
    value: 'violent',
    label: 'Violence/Gore',
    description: 'Graphic violence or disturbing imagery',
    common: true,
  },
  {
    value: 'stolen',
    label: 'Stolen Content',
    description: 'Content taken from another creator',
    common: true,
  },
  {
    value: 'copyright',
    label: 'Copyright Violation',
    description: 'Using content without permission',
    common: true,
  },
  {
    value: 'csam',
    label: 'Child Safety',
    description: 'Content exploiting or endangering minors',
  },
  {
    value: 'doxxing',
    label: 'Privacy Violation',
    description: 'Sharing private information without consent',
  },
  {
    value: 'illegal',
    label: 'Illegal Content',
    description: 'Content that violates laws',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Other issues not listed above',
    common: true,
  },
];

const ENTITY_LABELS = {
  discussion: 'Discussion',
  comment: 'Comment',
  user: 'User Profile',
  image: 'Image',
  video: 'Video',
  media: 'Media',
  profile_image: 'Profile Picture',
  banner: 'Banner Image',
  banner_image: 'Banner Image',
  thumbnail: 'Thumbnail',
};

/**
 * Report Dialog Component
 *
 * @param {Object} props
 * @param {string} props.entityType - Type of entity being reported
 * @param {string} props.entityId - ID of the entity being reported
 * @param {string} props.entityOID - Optional ObjectId for database reference
 * @param {string} props.mediaUrl - Optional URL of the media being reported
 * @param {string} props.mimeType - Optional mime type of the media
 * @param {string} props.contentPreview - Optional text preview of content
 * @param {string} props.parentDiscussionId - Optional parent discussion ID
 * @param {React.ReactNode} props.trigger - Optional custom trigger element
 * @param {React.ReactNode} props.children - Alternative to trigger prop
 */
export function ReportDialog({
  entityType,
  entityId,
  entityOID,
  mediaUrl,
  mimeType,
  contentPreview,
  parentDiscussionId,
  trigger,
  children,
}) {
  const { walletAddress, isAuthenticated } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Select reason, 2: Add description, 3: Success
  const [selectedReason, setSelectedReason] = useState('');
  const [description, setDescription] = useState('');
  const [showAllReasons, setShowAllReasons] = useState(false);

  const submitReport = useSubmitReport();

  // Check if user has already reported this entity
  const { data: reportCheck, isLoading: isCheckingReport } = useCheckReport(
    isAuthenticated ? entityType : null,
    isAuthenticated ? entityId : null,
    isAuthenticated ? entityOID : null
  );
  const hasAlreadyReported = reportCheck?.hasReported;

  const isMediaType = [
    'image',
    'video',
    'media',
    'profile_image',
    'banner',
    'banner_image',
    'thumbnail',
  ].includes(entityType);

  const allReasons = isMediaType ? MEDIA_REPORT_REASONS : REPORT_REASONS;
  const commonReasons = allReasons.filter((r) => r.common);
  const otherReasons = allReasons.filter((r) => !r.common);
  const displayedReasons = showAllReasons ? allReasons : commonReasons;

  const entityLabel = ENTITY_LABELS[entityType] || 'Content';

  const handleClose = () => {
    setOpen(false);
    // Reset after animation
    setTimeout(() => {
      setStep(1);
      setSelectedReason('');
      setDescription('');
      setShowAllReasons(false);
    }, 200);
  };

  const handleNext = () => {
    if (selectedReason) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async () => {
    if (!selectedReason) return;

    // Sanitize description to prevent XSS
    const sanitizedDescription = description.trim()
      ? DOMPurify.sanitize(description.trim(), { ALLOWED_TAGS: [] })
      : undefined;

    try {
      await submitReport.mutateAsync({
        entityType,
        entityId,
        entityOID,
        reason: selectedReason,
        description: sanitizedDescription,
        mediaUrl,
        mimeType,
        parentDiscussionId,
      });
      setStep(3); // Success
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  // Determine if this is a serious/priority report
  const isSeriousReport = [
    'csam',
    'illegal',
    'violent',
    'pornographic',
    'self_harm_promotion',
  ].includes(selectedReason);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || children || (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-red-500"
          >
            <Flag className="h-4 w-4 mr-1" />
            Report
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        {/* Not authenticated state */}
        {!isAuthenticated ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Flag className="h-5 w-5 text-red-500" />
                Sign In Required
              </DialogTitle>
              <DialogDescription>
                You need to be signed in to report content.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center py-8">
              <div className="h-16 w-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
                <LogIn className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              </div>
              <p className="text-center text-muted-foreground mb-6 max-w-sm">
                Please connect your wallet to report inappropriate content. This
                helps us maintain a safe community for everyone.
              </p>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            </div>
          </>
        ) : isCheckingReport ? (
          /* Loading state while checking if already reported */
          <div className="flex flex-col items-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Checking...</p>
          </div>
        ) : hasAlreadyReported ? (
          /* Already reported state */
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Already Reported
              </DialogTitle>
              <DialogDescription>
                You have already reported this {entityLabel.toLowerCase()}.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center py-8">
              <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-center text-muted-foreground mb-2 max-w-sm">
                Thank you for your report! Our moderation team is reviewing this
                content.
              </p>
              <p className="text-center text-muted-foreground text-sm mb-6 max-w-sm">
                You'll be notified if any action is taken based on your report.
              </p>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Header */}
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex items-center gap-2">
                {step === 3 ? (
                  <>
                    <Check className="h-5 w-5 text-green-500" />
                    Report Submitted
                  </>
                ) : (
                  <>
                    <Flag className="h-5 w-5 text-red-500" />
                    Report {entityLabel}
                  </>
                )}
              </DialogTitle>
              {step !== 3 && (
                <DialogDescription>
                  {step === 1
                    ? 'Select the reason that best describes the issue'
                    : 'Add additional details to help us understand the issue'}
                </DialogDescription>
              )}
            </DialogHeader>

            {/* Content */}
            <div className="flex-1 overflow-y-auto py-4 min-h-0">
              {/* Step 1: Select Reason */}
              {step === 1 && (
                <div className="space-y-4">
                  {/* Media Preview */}
                  {isMediaType && mediaUrl && (
                    <div className="rounded-lg overflow-hidden bg-muted max-h-32 flex items-center justify-center">
                      {entityType === 'video' ? (
                        <div className="flex items-center gap-2 text-muted-foreground py-6">
                          <Video className="h-6 w-6" />
                          <span>Video content</span>
                        </div>
                      ) : (
                        <img
                          src={mediaUrl}
                          alt="Content to report"
                          loading="lazy"
                          className="max-h-32 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                    </div>
                  )}

                  {/* Content preview */}
                  {contentPreview && !isMediaType && (
                    <div className="p-3 bg-muted/50 rounded-lg border text-sm">
                      <p className="text-muted-foreground text-xs mb-1">
                        Reporting:
                      </p>
                      <p className="line-clamp-2">{contentPreview}</p>
                    </div>
                  )}

                  <RadioGroup
                    value={selectedReason}
                    onValueChange={setSelectedReason}
                  >
                    <div className="space-y-2">
                      {displayedReasons.map((reason) => (
                        <div
                          key={reason.value}
                          className={cn(
                            'flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors',
                            selectedReason === reason.value
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:bg-muted/50'
                          )}
                          onClick={() => setSelectedReason(reason.value)}
                        >
                          <RadioGroupItem
                            value={reason.value}
                            id={reason.value}
                            className="mt-0.5"
                          />
                          <Label
                            htmlFor={reason.value}
                            className="flex-1 cursor-pointer"
                          >
                            <span className="font-medium">{reason.label}</span>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              {reason.description}
                            </p>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>

                  {!showAllReasons && otherReasons.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-muted-foreground"
                      onClick={() => setShowAllReasons(true)}
                    >
                      Show more options ({otherReasons.length} more)
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </div>
              )}

              {/* Step 2: Add Description */}
              {step === 2 && (
                <div className="space-y-4">
                  <div className="p-3 bg-muted/50 rounded-lg border">
                    <p className="text-sm">
                      <span className="font-medium">Reason: </span>
                      {
                        allReasons.find((r) => r.value === selectedReason)
                          ?.label
                      }
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">
                      Additional Details (Optional)
                    </Label>
                    <Textarea
                      id="description"
                      placeholder="Provide any additional context that might help us understand the issue better..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      maxLength={1000}
                      className="resize-none"
                    />
                    <p className="text-xs text-muted-foreground text-right">
                      {description.length}/1000 characters
                    </p>
                  </div>

                  {/* Warning for serious reports */}
                  {isSeriousReport && (
                    <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm">
                      <p className="flex items-center gap-2 font-medium text-red-600 dark:text-red-400">
                        <AlertTriangle className="h-4 w-4" />
                        Priority Report
                      </p>
                      <p className="mt-1 text-red-600/80 dark:text-red-400/80">
                        This type of report will be prioritized and reviewed by
                        our safety team.
                      </p>
                    </div>
                  )}

                  <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
                      False reports may result in action being taken against
                      your account.
                    </p>
                  </div>
                </div>
              )}

              {/* Step 3: Success */}
              {step === 3 && (
                <div className="flex flex-col items-center py-6 text-center">
                  <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                    <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    Thank you for your report
                  </h3>
                  <p className="text-muted-foreground mb-4 max-w-sm">
                    Our moderation team will review this content and take
                    appropriate action. We appreciate you helping keep our
                    community safe.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    You may not receive a direct response, but your report helps
                    us improve the platform.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 flex justify-between gap-2 pt-4 border-t">
              {step === 1 && (
                <>
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button onClick={handleNext} disabled={!selectedReason}>
                    Continue
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </>
              )}

              {step === 2 && (
                <>
                  <Button variant="outline" onClick={handleBack}>
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitReport.isPending}
                    variant="destructive"
                  >
                    {submitReport.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Flag className="h-4 w-4 mr-2" />
                        Submit Report
                      </>
                    )}
                  </Button>
                </>
              )}

              {step === 3 && (
                <Button className="w-full" onClick={handleClose}>
                  Done
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ReportDialog;
